import { Injectable, OnDestroy } from '@angular/core';
import { Product, ShareClass } from '@models';
import { Distribution } from '@products/models/distribution';
import { DistributionHistory } from '@products/models/distribution-history';
import { DistributionHistoryService } from '@products/services/distribution-history.service';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { SiteConfigService } from '@services/site-config.service';
import {
  ConfigurationId,
  DistributionState,
  DownloadableTableRowData,
  LatestDistribution,
  ProductDetailConfiguration,
  ProductType,
} from '@types';
import { Logger } from '@utils/logger';
import { forkJoin, Subject, Observable } from 'rxjs';
import distributionHistoricalData from '@graphql/pricing-distribution/distribution-history-international.graphql';
import distributionHistoricalDataUS from '@graphql/pricing-distribution/distribution-history-us.graphql';
import distributionLatestData from '@graphql/pricing-distribution/distribution-international.graphql';
import distributionLatestDataUS from '@graphql/pricing-distribution/distribution-us.graphql';
import { DISTRIBUTION_INITIAL_STATE } from './pricing-distribution.config';
import sortedUniq from 'lodash/sortedUniq';
import first from 'lodash/first';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { TranslateService } from '@shared/translate/translate.service';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { DebugService } from '@services/debug.service';
import { PagedRecords } from '@frk/eds-components';
import {
  FtCellObject,
  getCellObject,
  getColWidths,
} from '@products/services/excel-download.service';

const logger = Logger.getLogger('DistributionService');

@Injectable({
  providedIn: 'root',
})
export class DistributionService implements OnDestroy {
  private state: DistributionState;
  private configurationName: ConfigurationId;
  distributionState: Subject<DistributionState>;
  protected pagedRecords: PagedRecords<DistributionHistory>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private siteConfigDateFormat: string;
  /**
   * default pagesize for the tables
   */
  pageSize = 10;

  constructor(
    private distributionHistoryService: DistributionHistoryService,
    private siteConfigService: SiteConfigService,
    private fundOverviewService: FundOverviewService,
    private translateService: TranslateService,
    private caveatService: CaveatService,
    private debugService: DebugService
  ) {
    this.pagedRecords = new PagedRecords<DistributionHistory>(this.pageSize); // Page size set to '10', ref. by prod site.
    this.distributionState = new Subject();
    this.siteConfigDateFormat = this.siteConfigService
      .getGlobalConfig()
      ?.siteConfiguration?.site?.dateFormat.toUpperCase();
  }

  /**
   * Call register method of fund overview & distribution history service.
   * @param productDetailConfiguration the configuration received on page detail load
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.state = cloneDeep(DISTRIBUTION_INITIAL_STATE);

    forkJoin([
      this.fundOverviewService.register(
        this.siteConfigService.isSiteInternational()
          ? distributionLatestData
          : distributionLatestDataUS,
        {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }
      ),
      this.distributionHistoryService.register(
        this.siteConfigService.isSiteInternational()
          ? distributionHistoricalData
          : distributionHistoricalDataUS,
        {
          configurationName: this.getConfigurationName(),
          productType: ProductTypeParameter.MUTUAL_FUNDS,
          fundId: productDetailConfiguration.fundId,
          shareClassCode: productDetailConfiguration.shareClassCode,
        }
      ),
    ]).subscribe((mergedData: [Product, DistributionHistory[]]) => {
      logger.debug(mergedData);
      this.mapState(mergedData);
    });
  }

  /**
   * Split the merged details and map the overview & distribution data to the state accordingly.
   * Map the decorated config and any other setting to the state.
   * @param mergedData merged response for product & distribution histories.
   */
  mapState(mergedData: [Product, DistributionHistory[]]): void {
    const [overviewData, distributionHistories] = mergedData;

    this.state.fundName = overviewData.fundName;
    this.state.productType = overviewData.productType;

    this.state.siteConfig = this.siteConfigService.product?.site;
    this.state.isSiteIntl = this.siteConfigService.isSiteInternational();

    const shareClassDetail: ShareClass = overviewData.shareClasses[0];

    // *** US specific state assignments. ***
    if (!this.state.isSiteIntl) {
      // Map Distribution Schedules.
      this.state.schedules = {
        distributionIndicator: shareClassDetail.distributionIndicator,
        dividendFrequency: shareClassDetail.dividendFrequency,
        capgainFrequency: hasValue(shareClassDetail, 'capgainFrequency')
          ? shareClassDetail.capgainFrequency
          : null,
      };

      // Map SalesChargeBreakPoint.
      this.state.salesChargeBreakPoints =
        shareClassDetail.salesChargeBreakPoints;

      // Map Yields.
      this.state.yields = shareClassDetail.yields;
    }

    this.state.latestConfig.isSgHk = this.siteConfigService.isSgHk();
    this.state.latestConfig.showCanadaDistForEtfMfMmf = this.showCanadaDistForEtfMfMmf(
      this.state.productType as ProductType
    );
    this.state.latestConfig.isSingleShareClass =
      shareClassDetail.isSingleShareClass;
    this.state.latestConfig.shareClassName = shareClassDetail.shareClassName;
    this.state.latestConfig.shareClassInceptionDate =
      shareClassDetail.inceptionDate;
    this.state.latestConfig.shareClassInceptionDateStd =
      shareClassDetail.inceptionDateStd;
    this.state.latestConfig.currencyCode = shareClassDetail.shareClassCurrency;
    this.state.latestConfig.navValue =
      distributionHistories[0]?.exDividendNavValue;
    this.state.latestConfig.hideExDistDate = this.hideExDistDateForCountry();

    if (shareClassDetail.distribution.length) {
      let distributionDetail: Distribution;
      // NGC-8383: Config for estimated values in case of US.
      // Assign boolean 'true', if estimate is provided with 'true' value, else 'false'.
      // Check it for only "zero/first" row, as it would come only in either of the rows, if available. Ref. PDS.
      // Fallback for distribution would always be 'zero' row for both US/Intl and estimate as 'false' only in case of US.
      if (!this.state.isSiteIntl) {
        if (shareClassDetail.distribution[0].estimateFlag === 'true') {
          distributionDetail = shareClassDetail.distribution[0];
          this.state.latestConfig.estimateFlag = true;
        } else if (shareClassDetail.distribution[1]?.estimateFlag === 'true') {
          distributionDetail = shareClassDetail.distribution[1];
          this.state.latestConfig.estimateFlag = true;
        } else {
          distributionDetail = shareClassDetail.distribution[0];
          this.state.latestConfig.estimateFlag = false;
        }
      } else {
        distributionDetail = shareClassDetail.distribution[0];
      }

      this.state.latestConfig.asOfDate =
        distributionDetail.distributionAsOfDate;
      this.state.latestConfig.asOfDateYear =
        distributionDetail.distributionAsOfDateYear;
      this.state.latestConfig.incomeRecordDate =
        distributionDetail.incomeDistributionRecordDate;
      this.state.latestConfig.incomeExDividendDate =
        distributionDetail.incomeDistributionExDividendDate;
      this.state.latestConfig.incomeDividendDate =
        distributionDetail.incomeDistributionPayableDate;
      this.state.latestConfig.incomeDistributionAmount =
        distributionDetail.incomeDistributionAmount;
      this.state.latestConfig.divDistributionIncome =
        distributionDetail.divNetDistributableIncome;
      this.state.latestConfig.dividendCapital =
        distributionDetail.divPaidCapital;
      if (this.siteConfigService.isGermany()) {
        this.state.latestConfig.totalDistributionAmountMonthly =
          distributionDetail.totalDistributionAmountMonthly;
      }

      // NGC-13460: Canada - Canadian ETFs - Latest Distribution & Historical Distributions
      if (this.state.latestConfig.showCanadaDistForEtfMfMmf) {
        this.state.latestConfig.reInvestmentAmount =
          distributionDetail.reInvestmentAmount;
        this.state.latestConfig.totalDistributionAmountMonthly =
          distributionDetail.totalDistributionAmountMonthly;
        this.state.latestConfig.returnOfCapitalAmount =
          distributionDetail.returnOfCapitalAmount;
        this.state.latestConfig.capitalGains = distributionDetail.capitalGains;
      }

      // Map US specific fields.
      if (!this.state.isSiteIntl) {
        this.state.latestConfig.navDate = shareClassDetail.nav.navAsOfDate;
        this.state.latestConfig.reInvestmentDate =
          distributionDetail.reInvestmentDate;
        this.state.latestConfig.capgainsRecordDate =
          distributionDetail.capitalGainRecordDate;
        this.state.latestConfig.capgainsExDividendDate =
          distributionDetail.capitalGainExDividendDate;
        this.state.latestConfig.capgainsReinvestmentDate =
          distributionDetail.capitalGainReInvestmentDate;
        this.state.latestConfig.capgainsPayableDate =
          distributionDetail.capitalGainPayableDate;
        this.state.latestConfig.reInvestmentAmount =
          distributionDetail.reInvestmentAmount;
        this.state.latestConfig.longTermCapitalGain =
          distributionDetail.longTermCapitalGain;
        this.state.latestConfig.shortTermCapitalGain =
          distributionDetail.shortTermCapitalGain;
        this.state.latestConfig.taxableExemptIncome =
          distributionDetail.taxableExemptIncomeAmount;
        this.state.latestConfig.returnOfCapitalAmount =
          distributionDetail.returnOfCapitalAmount;
        this.state.latestConfig.totalDistributionAmountMonthly =
          distributionDetail.totalDistributionAmountMonthly;

        // ***** LOGIC: SHOW INCOME OR CAPGAINS DATE. REF: PDS *****
        // In any case, only one of them will be present in feed.
        // And, preference will be given to income.
        // And, out of all income dates, ex-div will always be populated while rest can be null for non-capgains.
        // So, draft condition based upon ex-div date only.
        // Further a base check is applied for only "non-estimated" cases.
        this.state.latestConfig.showIncomeOverCapgains =
          !this.state.latestConfig.estimateFlag &&
          this.state.latestConfig.incomeExDividendDate
            ? true
            : false;
      }
    }

    if (distributionHistories.length) {
      this.state.tableConfig.dividendYears = sortedUniq(
        distributionHistories.map((distributionHistory) =>
          distributionHistory.distributionAsOfDateYear.toString()
        )
      );
      // WDE-1621: Logic implemented to remove current year distribution from distribution history
      // Add current year distribution from distribution history
      if (!this.state.isSiteIntl) {
        // WDE-3798: Logic implemented to display current year data in Distribution table
        const currentYear: string = new Date().getFullYear().toString();
        const latestDistributionHistory: DistributionHistory[] = distributionHistories.filter(
          (distributionHistory: DistributionHistory) =>
            distributionHistory.distributionAsOfDateYear === currentYear
        );

        // Add current year historical record to lastest records
        if (latestDistributionHistory.length) {
          this.state.historicalData = distributionHistories.filter(
            (distributionHistory: DistributionHistory) =>
              distributionHistory.distributionAsOfDateYear !== currentYear
          );
          this.state.tableConfig.dividendYears.shift();

          const latestDistributions: LatestDistribution[] = this.getFormattedDistributionTableRecords(
            latestDistributionHistory
          );
          // Append estimated record to latest records
          if (this.state.latestConfig.estimateFlag) {
            latestDistributions.unshift(this.state.latestConfig);
          }
          this.state.latestDistributions = latestDistributions;
        } else {
          this.state.historicalData = distributionHistories;
          // WDE-4398: uncommenting the below code for US as we need to display only current year record on distribution table
          this.state.latestDistributions.push(this.state.latestConfig);
        }
      } else {
        this.state.historicalData = distributionHistories;
      }
      this.state.tableConfig.dividendYears.push('SINCE_INCEPTION'); // to be always set as last option.

      // make default selection to Since Inception.
      this.state.tableConfig.selectedYear = this.state.tableConfig.dividendYears[
        this.state.tableConfig.dividendYears.length - 1
      ];

      this.createDistributionTable(this.state.tableConfig.selectedYear);
    }

    this.state.hasLoadedFullFeed = true;
    this.distributionState.next(this.state);
  }

  setConfigurationName(configurationName: ConfigurationId): void {
    this.configurationName = configurationName;
  }

  getConfigurationName(): ConfigurationId {
    return this.configurationName;
  }

  /**
   * ******************** Distribution history table helper methods ********************
   */

  /**
   * Filter the historical distribution table data as per the year selected.
   * @param selectedYear distribution year selected
   */
  createDistributionTable(selectedYear: string): void {
    let filteredRecords: DistributionHistory[] = [];

    if (selectedYear === 'SINCE_INCEPTION') {
      filteredRecords = cloneDeep(this.state.historicalData);
    } else {
      filteredRecords = cloneDeep(
        this.state.historicalData.filter(
          (distributionHistory) =>
            distributionHistory.distributionAsOfDateYear === selectedYear
        )
      );
    }
    this.pagedRecords.records = filteredRecords;
    this.pagedRecords.pageNumber = 1; // post new selection, set page number to 1.

    this.state.tableConfig.selectedYear = selectedYear;
    this.state.tableConfig.startDate = this.getHistoricalStartDate();
    this.state.tableConfig.endDate = this.getHistoricalEndDate();

    // expand tables for Smarsh capture
    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean) => {
        if (expandAll) {
          this.pageSize = this.pagedRecords.expandAll();
        }
      });

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this.distributionState.next(this.state);
    }
  }

  /**
   * Calculate historical start date.
   */
  getHistoricalStartDate(): string {
    // COREWEB-3143 : first year of data not alway inception year
    const isInceptionYear =
      this.state.latestConfig.shareClassInceptionDateStd.indexOf(
        this.state.tableConfig.selectedYear
      ) === 0;

    if (
      isInceptionYear ||
      this.state.tableConfig.selectedYear === 'SINCE_INCEPTION'
    ) {
      return this.state.latestConfig.shareClassInceptionDate;
    }

    return dayjs(
      `${this.state.tableConfig.selectedYear}-01-01`,
      'YYYY-MM-DD'
    ).format('L');
  }

  /**
   * Calculate historical end date.
   */
  getHistoricalEndDate(): string {
    const lastYear = first(this.state.tableConfig.dividendYears);
    if (
      this.state.tableConfig.selectedYear === lastYear ||
      this.state.tableConfig.selectedYear === 'SINCE_INCEPTION'
    ) {
      if (!this.state.isSiteIntl) {
        return dayjs(`${lastYear}-12-31`).format(
          this.siteConfigDateFormat || 'L'
        );
      }

      return dayjs().format(this.siteConfigDateFormat || 'L');
    }

    return dayjs(`${this.state.tableConfig.selectedYear}-12-31`).format(
      this.siteConfigDateFormat || 'L'
    );
  }

  /**
   * Return the boolean value for preference on income over capgain values.
   * @param distributionHistory distribution history object
   */
  showIncomeOverCapgains(distributionHistory: DistributionHistory): boolean {
    return distributionHistory.incomeDistributionExDividendDate ? true : false;
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   */
  triggerDownloadAction(): void {
    const mappedHeader: Map<string, string> = this.mapDataHeader(
      this.state.isSiteIntl,
      this.state.latestConfig.isSgHk,
      this.state.latestConfig.showCanadaDistForEtfMfMmf,
      this.state.latestConfig.hideExDistDate
    );

    const mappedBody: DownloadableTableRowData[] = this.mapDownloadData(
      this.getTotalFilteredRecords(),
      this.state.isSiteIntl,
      this.state.latestConfig.isSgHk,
      this.state.latestConfig.showCanadaDistForEtfMfMmf,
      this.state.latestConfig.hideExDistDate
    );

    const mappedFullRecords: FtCellObject[][] = [
      Object.keys(mappedBody[0]).map((data) =>
        getCellObject(this.translateService.instant(mappedHeader.get(data)))
      ),
    ];

    mappedBody.forEach((data) => {
      mappedFullRecords.push(Object.values(data));
    });

    const fundName = [];
    const shareClassSection = [];
    const endDateStartDateSection = [];

    fundName.push([this.state.fundName]);

    if (!this.state.latestConfig.isSingleShareClass) {
      shareClassSection.push([
        `${this.translateService.instant('pds.SHARE_CLASS_NAME_STD')}`,
        this.state.latestConfig.shareClassName,
      ]);
    }

    shareClassSection.push([
      `${this.translateService.instant('products.inception-date')}`,
      this.state.latestConfig.shareClassInceptionDate,
    ]);
    shareClassSection.push([
      `${this.translateService.instant('pds.SHARE_CLASS_CURRENCY')}`,
      this.state.historicalData[0].currencyCode,
    ]);

    endDateStartDateSection.push([
      `${this.translateService.instant(
        'products.distribution-excel-download-dividends'
      )} : ${this.translateService.instant('products.calendar-from')} ${
        this.state.tableConfig.startDate
      } ${this.translateService.instant('products.calendar-to')} ${
        this.state.tableConfig.endDate
      }`,
    ]);

    forkJoin([
      this.getProximalCaveat('HistoricalDividendDataExcelBottom'),
      this.getProximalCaveat('HistoricalDividendDataExcelTop'),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [string[][], string[][]] = data;
      this.exportToExcel(
        mappedFullRecords,
        topCaveat,
        bottomCaveat,
        fundName,
        shareClassSection,
        endDateStartDateSection
      );
    });
  }

  /**
   * get the proximal caveats for a particular placement
   */
  getProximalCaveat(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.map((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  /**
   * Return mapped header for corresponding column data.
   * @param isSiteIntl boolean flag for site international or US
   */
  mapDataHeader(
    isSiteIntl: boolean,
    isSgHk: boolean,
    showCanadaDistForEtfMfMmf: boolean,
    hideExDistDate: boolean
  ): Map<string, string> {
    const mappedHeader = new Map<string, string>();

    if (isSiteIntl) {
      mappedHeader.set('currency', 'pds.SHARE_CLASS_CURRENCY');
      mappedHeader.set('recordDate', 'pds.INCM_DIST_RECORD_DATE');
      if (!hideExDistDate) {
        mappedHeader.set('exDividendDate', 'pds.INCM_DIST_EX_DIV_DATE');
      }
      mappedHeader.set('distributionDate', 'pds.INCM_DIST_PAYABLE_DATE');
      mappedHeader.set('nav', 'pds.NAV_VALUE');
      mappedHeader.set('distributionPerUnit', 'pds.INCM_DIST_AMT');
      if (isSgHk) {
        mappedHeader.set('divDistributionIncome', 'pds.INCM_DIST_INCOME');
        mappedHeader.set('dividendCapital', 'pds.INCM_DIV_CAPITAL');
      } else if (showCanadaDistForEtfMfMmf) {
        if (this.state.productType === 'ETFs') {
          mappedHeader.set('exDividendDate', 'products.dist-ex-dist-date');
          mappedHeader.set(
            'incomeDistributionAmount',
            'products.dist-cash-dist-per-unit'
          );
          mappedHeader.set(
            'reInvestmentAmount',
            'products.dist-rein-dist-per-unit'
          );
          mappedHeader.set(
            'totalDistributionAmountMonthly',
            'products.dist-total-dist-per-unit'
          );
          // mappedHeader.set('exDividendDate', 'pds.EX_DIST_DATE');
          // mappedHeader.set('incomeDistributionAmount', 'pds.CASH_DIST_PER_UNIT');
          // mappedHeader.set('reInvestmentAmount', 'pds.REIN_DIST_PER_UNIT');
          // mappedHeader.set(
          //   'totalDistributionAmountMonthly',
          //   'pds.TOTAL_DIST_PER_UNIT'
          // );
        } else if (
          this.state.productType === 'Mutual Funds' ||
          this.state.productType === 'Money Market Funds'
        ) {
          mappedHeader.set('currency', 'pds.SHARE_CLASS_CURRENCY');
          mappedHeader.set('recordDate', 'pds.INCM_DIST_RECORD_DATE');
          mappedHeader.set('exDividendDate', 'pds.INCM_DIST_PAYABLE_DATE');
          mappedHeader.set('exDividendNavValue', 'products.dist-dist-price');
          mappedHeader.set(
            'incomeDistributionAmount',
            'products.dist-inv-income'
          );
          mappedHeader.set(
            'returnOfCapitalAmount',
            'products.dist-return-of-cap'
          );
          mappedHeader.set('longTermCapitalGain', 'products.dist-capgains');
          mappedHeader.set(
            'totalDistributionAmountMonthly',
            'products.dist-total-dist-per-unit'
          );
        } else {
          this.mapXlsHeaderForIntl(mappedHeader);
        }
      }
    } else {
      this.mapXlsHeaderForIntl(mappedHeader);
    }

    return mappedHeader;
  }

  private mapXlsHeaderForIntl(mappedHdr: Map<string, string>): void {
    mappedHdr.set('incomeDistributionRecordDate', 'pds.INCM_DIST_RECORD_DATE');
    mappedHdr.set('capitalGainRecordDate', 'pds.INCM_DIST_RECORD_DATE');
    mappedHdr.set(
      'incomeDistributionExDividendDate',
      'pds.INCM_DIST_EX_DIV_DATE'
    );
    mappedHdr.set('capitalGainExDividendDate', 'pds.INCM_DIST_EX_DIV_DATE');
    if (this.state.productType !== ConfigurationId.CEF) {
      mappedHdr.set('reInvestmentDate', 'pds.REINVESTMENT_DATE');
    }
    mappedHdr.set('capitalGainReInvestmentDate', 'pds.REINVESTMENT_DATE');
    mappedHdr.set(
      'incomeDistributionExPayableDate',
      'pds.INCM_DIST_PAYABLE_DATE'
    );
    mappedHdr.set('capitalGainPayableDate', 'pds.INCM_DIST_PAYABLE_DATE');
    if (this.state.productType !== ConfigurationId.CEF) {
      mappedHdr.set('reInvestmentAmount', 'pds.REINVESTMENT_AMT');
    }

    if (
      this.state.productType !== ConfigurationId.TOF &&
      this.state.productType !== ConfigurationId.CEF
    ) {
      mappedHdr.set('longTermCapitalGain', 'pds.CAPGAIN_AMT_LONG_TERM');
      mappedHdr.set('shortTermCapitalGain', 'pds.CAPGAIN_AMT_SHRT_TERM');
    }
    if (this.state.productType !== ConfigurationId.CEF) {
      mappedHdr.set('incomeDistributionAmount', 'pds.INCOME_AMOUNT');
    }
    mappedHdr.set(
      'totalDistributionAmountMonthly',
      'pds.TOTAL_DISTRIBUTION_PER_SHARE'
    );
  }

  /**
   * Return mapped data as per downloadable action.
   * @param totalRecords total filtered records
   * @param isSiteIntl boolean flag for site international or US
   */
  mapDownloadData(
    totalRecords: DistributionHistory[],
    isSiteIntl: boolean,
    isSgHk: boolean,
    showCanadaDistForEtfMfMmf: boolean,
    hideExDistDate: boolean
  ): DownloadableTableRowData[] {
    const mappedData: DownloadableTableRowData[] = [];

    if (isSiteIntl) {
      if (isSgHk) {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            exDividendDate: record.incomeDistributionExDividendDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: record.incomeDistributionAmount,
            divDistributionIncome: record.divDistributionIncome,
            dividendCapital: record.dividendCapital,
          });
        });
      } else if (showCanadaDistForEtfMfMmf) {
        if (this.state.productType === 'ETFs') {
          totalRecords.forEach((record) => {
            mappedData.push({
              currency: record.currencyCode,
              recordDate: record.incomeDistributionRecordDate,
              exDividendDate: record.incomeDistributionExDividendDate,
              distributionDate: record.incomeDistributionExPayableDate,
              incomeDistributionAmount: record.incomeDistributionAmount,
              reInvestmentAmount: record.reInvestmentAmount,
              totalDistributionAmountMonthly:
                record.totalDistributionAmountMonthly,
            });
          });
        } else if (
          this.state.productType === 'Mutual Funds' ||
          this.state.productType === 'Money Market Funds'
        ) {
          totalRecords.forEach((record) => {
            mappedData.push({
              currency: record.currencyCode,
              recordDate: record.incomeDistributionRecordDate,
              exDividendDate: record.incomeDistributionExDividendDate,
              exDividendNavValue: record.exDividendNavValue,
              incomeDistributionAmount: record.incomeDistributionAmount,
              returnOfCapitalAmount: record.returnOfCapitalAmount,
              longTermCapitalGain: record.longTermCapitalGain,
              totalDistributionAmountMonthly:
                record.totalDistributionAmountMonthly,
            });
          });
        }
      } else if (hideExDistDate) {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: record.incomeDistributionAmount,
          });
        });
      } else {
        totalRecords.forEach((record) => {
          mappedData.push({
            currency: record.currencyCode,
            recordDate: record.incomeDistributionRecordDate,
            exDividendDate: record.incomeDistributionExDividendDate,
            distributionDate: record.incomeDistributionExPayableDate,
            nav: record.exDividendNavValue,
            distributionPerUnit: this.siteConfigService.isGermany()
              ? record.totalDistributionAmountMonthly
              : record.incomeDistributionAmount,
          });
        });
      }
    } else {
      totalRecords.forEach((record) => {
        if (this.showIncomeOverCapgains(record)) {
          const ovrCapGains = {
            incomeDistributionRecordDate: record.incomeDistributionRecordDate,
            incomeDistributionExDividendDate:
              record.incomeDistributionExDividendDate,
            reInvestmentDate: record.reInvestmentDate,
            incomeDistributionExPayableDate:
              record.incomeDistributionExPayableDate,
            reInvestmentAmount: record.reInvestmentAmount,
            longTermCapitalGain: record.longTermCapitalGain,
            shortTermCapitalGain: record.shortTermCapitalGain,
            incomeDistributionAmount: record.incomeDistributionAmount,
            totalDistributionAmountMonthly:
              record.totalDistributionAmountMonthly,
          };
          // removing the column for CEF WDE-6698
          // Refactored code WDE-6698 for WDE-7219 for remove duplicate records in excel export historical data

          if (this.state.productType === ConfigurationId.CEF) {
            delete ovrCapGains.reInvestmentDate;
            delete ovrCapGains.reInvestmentAmount;
            delete ovrCapGains.incomeDistributionAmount;
            delete ovrCapGains.longTermCapitalGain;
            delete ovrCapGains.shortTermCapitalGain;
            mappedData.push(ovrCapGains);
          } else if (this.state.productType === ConfigurationId.TOF) {
            delete ovrCapGains.longTermCapitalGain;
            delete ovrCapGains.shortTermCapitalGain;
            mappedData.push(ovrCapGains);
          } else {
            mappedData.push(ovrCapGains);
          }
        } else {
          mappedData.push({
            capitalGainRecordDate: record.capitalGainRecordDate,
            capitalGainExDividendDate: record.capitalGainExDividendDate,
            capitalGainReInvestmentDate: record.capitalGainReInvestmentDate,
            capitalGainPayableDate: record.capitalGainPayableDate,
            reInvestmentAmount: record.reInvestmentAmount,
            longTermCapitalGain: record.longTermCapitalGain,
            shortTermCapitalGain: record.shortTermCapitalGain,
            incomeDistributionAmount: record.incomeDistributionAmount,
            totalDistributionAmountMonthly:
              record.totalDistributionAmountMonthly,
          });
        }
      });
    }

    return mappedData;
  }

  /**
   * Export the table data to excel and auto download.
   * @param data full record for excel
   */
  exportToExcel(
    data: FtCellObject[][],
    topCaveat: string[][],
    bottomCaveat: string[][],
    fundName: string[][],
    shareClassSection: string[][],
    endDateStartDateSection: string[][]
  ): void {
    import('xlsx').then((xlsx) => {
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();

      workSheet['!cols'] = getColWidths(data);

      xlsx.utils.sheet_add_aoa(workSheet, fundName, {
        origin: 'A' + (topCaveat.length + 2),
      });

      xlsx.utils.sheet_add_aoa(workSheet, shareClassSection, {
        origin: 'A' + (topCaveat.length + fundName.length + 3),
      });

      xlsx.utils.sheet_add_aoa(workSheet, endDateStartDateSection, {
        origin:
          'A' +
          (topCaveat.length + fundName.length + shareClassSection.length + 4),
      });

      xlsx.utils.sheet_add_aoa(workSheet, data, {
        origin:
          'A' +
          (topCaveat.length +
            fundName.length +
            shareClassSection.length +
            endDateStartDateSection.length +
            6),
      });

      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin:
          'A' +
          (data.length +
            8 +
            topCaveat.length +
            fundName.length +
            endDateStartDateSection.length +
            shareClassSection.length),
      });

      xlsx.utils.book_append_sheet(
        workBook,
        workSheet,
        this.translateService.instant(
          'products.distribution-history-report-tabname'
        )
      );
      xlsx.writeFile(
        workBook,
        this.translateService.instant(
          'products.distribution-history-report-filename'
        )
      );
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  fitToColumn(data: string[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(...data.map((a2) => a2[i].toString().length)),
    }));
  }

  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page records as observable.
   */
  getCurrentPage$(): Observable<DistributionHistory[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }

  /**
   * Return total filtered records.
   */
  getTotalFilteredRecords(): DistributionHistory[] {
    return this.pagedRecords.records;
  }

  showCanadaDistForEtfMfMmf(prodType: ProductType): boolean {
    return (
      this.siteConfigService.isCanada() &&
      (prodType === 'ETFs' ||
        prodType === 'Mutual Funds' ||
        prodType === 'Money Market Funds')
    );
  }

  hideExDistDateForCountry(): boolean {
    const countriesWithHiddenExDistDate = ['AU'];
    return countriesWithHiddenExDistDate.includes(
      this.siteConfigService?.product?.site?.country
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Create getFormattedDistributionTable from distribution history.
   * @param latestDistributionHistory current year distributions
   */
  getFormattedDistributionTableRecords(
    latestDistributionHistory: DistributionHistory[]
  ): LatestDistribution[] {
    const distributions: LatestDistribution[] = [];
    latestDistributionHistory.forEach(
      (latestDistribution: DistributionHistory) => {
        const distribution: LatestDistribution = {
          incomeRecordDate: latestDistribution.incomeDistributionRecordDate,
          capgainsRecordDate: latestDistribution.capitalGainRecordDate,
          incomeExDividendDate:
            latestDistribution.incomeDistributionExDividendDate,
          capgainsExDividendDate: latestDistribution.capitalGainExDividendDate,
          reInvestmentDate: latestDistribution.reInvestmentDate,
          capgainsReinvestmentDate:
            latestDistribution.capitalGainReInvestmentDate,
          incomeDividendDate:
            latestDistribution.incomeDistributionExPayableDate,
          capgainsPayableDate: latestDistribution.capitalGainPayableDate,
          reInvestmentAmount: latestDistribution.reInvestmentAmount,
          longTermCapitalGain: latestDistribution.longTermCapitalGain,
          shortTermCapitalGain: latestDistribution.shortTermCapitalGain,
          returnOfCapitalAmount: latestDistribution.returnOfCapitalAmount,
          totalDistributionAmountMonthly:
            latestDistribution.totalDistributionAmountMonthly,
          incomeDistributionAmount: latestDistribution.incomeDistributionAmount,
          estimateFlag: false, //  historical record will be actual not estimated
          showIncomeOverCapgains: latestDistribution.incomeDistributionExDividendDate
            ? true
            : false,
        };
        distributions.push(distribution);
      }
    );
    return distributions;
  }
}
